import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { UserProfile, AuthContextType } from '../types/auth';
import { redirectToLogin } from '../utils/auth-redirect';
import { warmMailAssertion } from '../services/mailAuth';

const AuthContext = createContext<AuthContextType>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    login: () => {},
    logout: () => {},
});

export function useAuth(): AuthContextType {
    return useContext(AuthContext);
}

interface Props {
    children: React.ReactNode;
}

export function AuthProvider({ children }: Props) {
    const [user, setUser] = useState<UserProfile | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const fetchUser = useCallback(async () => {
        try {
            // Check SWA authentication
            const authResponse = await fetch('/.auth/me');
            const authData = await authResponse.json();

            if (!authData.clientPrincipal) {
                setUser(null);
                setIsLoading(false);
                return;
            }

            // Fetch user profile from API
            const profileResponse = await fetch('/api/v1/auth/profile');
            if (!profileResponse.ok) {
                setUser(null);
                setIsLoading(false);
                return;
            }

            const profileData = await profileResponse.json();
            if (profileData.success) {
                setUser(profileData.data);
                // Mail goes out with the person's own Microsoft token (SEC-005); fetch it
                // silently now so the first send needs no sign-in window.
                if (import.meta.env.PROD) warmMailAssertion();
            }
        } catch (err) {
            console.error('Auth check failed:', err);
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    // Periodically check if the SWA session is still valid (every 5 minutes)
    useEffect(() => {
        if (!user) return;

        const checkSession = async () => {
            try {
                const res = await fetch('/.auth/me');
                const data = await res.json();
                if (!data.clientPrincipal) {
                    redirectToLogin();
                }
            } catch {
                // Network error — skip, will retry next interval
            }
        };

        const interval = setInterval(checkSession, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [user]);

    const login = useCallback(() => {
        redirectToLogin();
    }, []);

    const logout = useCallback(() => {
        window.location.href = '/.auth/logout';
    }, []);

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated: !!user,
            isLoading,
            login,
            logout,
        }}>
            {children}
        </AuthContext.Provider>
    );
}
